import { useState, useEffect } from 'react';
import { DialogTrigger, Dialog, Modal, Heading } from 'react-aria-components';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';

import { eachMinuteOfInterval, set, format } from 'date-fns';
import { useAtom } from 'jotai';

import { updatePickupDropoffReservations } from '@/api/updatePickupDropoffReservations';
import Button from '@/components/Button/Button';
import DateSelect from '@/components/reservations/transport/DateSelect';
import SelectField from '@/components/SelectField/SelectField';
import { NotificationAtom } from '@/jotai/jotai';
import { PickupDropoffReservation } from '@/types/pickupDropoff';
import {
  UpdatePickupDropoffReservationsRequest,
  UpdatePickupDropoffReservation,
} from '@/types/pickupDropoff';
import { Vehicle } from '@/types/vehicle';

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  afterSubmit: () => void;
  vehicles: Vehicle[];
  reservations: PickupDropoffReservation[];
};

const getTimeOptions = () => {
  const start = set(new Date(), { hours: 0, minutes: 0 });
  const end = set(new Date(), { hours: 23, minutes: 30 });

  return eachMinuteOfInterval({ start, end }, { step: 30 }).map((time) => ({
    value: format(time, 'HH:mm:ss'), // hh:mm:ssで送信するため
    label: format(time, 'HH:mm'),
  }));
};

const UpdatePickupDropoffReservationDialog = ({
  isOpen,
  setIsOpen,
  afterSubmit,
  vehicles,
  reservations,
}: Props) => {
  const [selectedDate, setSelectedDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));

  const [_, setNotification] = useAtom(NotificationAtom);

  const { handleSubmit, control, reset, getValues } =
    useForm<UpdatePickupDropoffReservationsRequest>({
      defaultValues: {
        reservations: reservationsToFormData(reservations, selectedDate),
      },
    });

  const { fields } = useFieldArray<UpdatePickupDropoffReservationsRequest>({
    control,
    name: 'reservations',
  });

  const onSubmit: SubmitHandler<UpdatePickupDropoffReservationsRequest> = async (data) => {
    const convertedData = {
      reservations: data.reservations.map((reservation) => ({
        id: reservation.id,
        pickup_info:
          reservation.pickup_info &&
          reservation.pickup_info.time &&
          reservation.pickup_info.vehicle_id
            ? {
                time: reservation.pickup_info.time,
                vehicle_id: Number(reservation.pickup_info.vehicle_id),
              }
            : undefined,
        dropoff_info:
          reservation.dropoff_info &&
          reservation.dropoff_info.time &&
          reservation.dropoff_info.vehicle_id
            ? {
                time: reservation.dropoff_info.time,
                vehicle_id: Number(reservation.dropoff_info.vehicle_id),
              }
            : undefined,
      })),
    };

    try {
      const result = await updatePickupDropoffReservations(convertedData);
      if (result.status === 200) {
        setNotification({
          headingLevel: 'h2',
          type: 'success',
          bannerStyle: 'standard',
          title: '配車情報を更新しました',
          message: '配車情報を更新しました',
        });
      } else {
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: '配車情報の更新に失敗しました',
          message: '配車情報の更新に失敗しました',
        });
      }
    } catch (error) {
      setNotification({
        headingLevel: 'h2',
        type: 'error',
        bannerStyle: 'standard',
        title: '配車情報の更新に失敗しました',
        message: '配車情報の更新に失敗しました',
      });
    }

    setIsOpen(false);
    afterSubmit();
  };

  function reservationsToFormData(
    pickupDropoffReservations: PickupDropoffReservation[],
    date: string
  ): UpdatePickupDropoffReservation[] {
    const updateReservations: UpdatePickupDropoffReservation[] = pickupDropoffReservations
      .filter((reservation) => reservation.reservation_date === date)
      .map((reservation) => ({
        id: reservation.id,
        pickup_info: reservation.pickup_info
          ? {
              time: reservation.pickup_info.time,
              vehicle_id: reservation.pickup_info.vehicle_id || 0,
            }
          : undefined,
        dropoff_info: reservation.dropoff_info
          ? {
              time: reservation.dropoff_info.time,
              vehicle_id: reservation.dropoff_info.vehicle_id || 0,
            }
          : undefined,
      }));
    return updateReservations;
  }

  useEffect(() => {
    reset({
      reservations: reservationsToFormData(reservations, selectedDate),
    });
  }, [selectedDate]);

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={() => setIsOpen(false)}
    >
      <Modal className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <Dialog className="max-h-[90vh] w-[95vw] max-w-[700px] overflow-hidden rounded-lg border border-gray-400 bg-white p-4 shadow-lg outline-none">
          <Heading
            level={2}
            className="mb-6 text-xl font-bold"
          >
            配車登録
          </Heading>
          <DateSelect
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mt-4 max-h-[60vh] overflow-y-auto">
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="mb-4"
                >
                  <div className="mb-2 flex items-center">
                    <span className="text-sm font-semibold">
                      {
                        reservations.find(
                          (reservation) => reservation.id === getValues(`reservations.${index}.id`)
                        )?.child_name
                      }
                    </span>
                  </div>

                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <p className="mb-1 text-xs text-gray-600">来所時刻</p>
                      <SelectField
                        name={`reservations.${index}.pickup_info.time`}
                        control={control}
                        items={getTimeOptions()}
                        defaultValueLabel="時刻選択"
                        disabled={!getValues(`reservations.${index}.pickup_info`) === undefined}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <p className="mb-1 text-xs text-gray-600">来所車両</p>
                      <SelectField
                        name={`reservations.${index}.pickup_info.vehicle_id`}
                        control={control}
                        items={vehicles.map((vehicle) => ({
                          value: vehicle.id.toString(),
                          label: vehicle.name,
                        }))}
                        defaultValueLabel="車両選択"
                        disabled={!getValues(`reservations.${index}.pickup_info`) === undefined}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <p className="mb-1 text-xs text-gray-600">退所時刻</p>
                      <SelectField
                        name={`reservations.${index}.dropoff_info.time`}
                        control={control}
                        items={getTimeOptions()}
                        defaultValueLabel="時刻選択"
                        disabled={getValues(`reservations.${index}.dropoff_info`) === undefined}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <p className="mb-1 text-xs text-gray-600">退所車両</p>
                      <SelectField
                        name={`reservations.${index}.dropoff_info.vehicle_id`}
                        control={control}
                        items={vehicles.map((vehicle) => ({
                          value: vehicle.id.toString(),
                          label: vehicle.name,
                        }))}
                        defaultValueLabel="車両選択"
                        disabled={getValues(`reservations.${index}.dropoff_info`) === undefined}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-row items-center justify-center space-x-4">
              <Button
                type="button"
                size="lg"
                variant="outline"
                onClick={() => setIsOpen(false)}
              >
                戻る
              </Button>
              <Button
                type="submit"
                size="lg"
                variant="solid-fill"
              >
                決定
              </Button>
            </div>
          </form>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
};

export default UpdatePickupDropoffReservationDialog;
